import {
  fork,
  put,
  all,
  call,
  take,
  cancel,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import i18n from '../../i18n';
import toastr from 'toastr';
import { toDateFirebase } from '../../helpers/sharedFunction';
import types from './actionTypes';
import * as actions from './actions';

const db = getFirestore(rsf.app);

const aiAgentTransformer = (payload) => {
  const aiAgents = [];
  payload.forEach((doc) => {
    const data = doc.data();
    aiAgents.push({
      id: doc.id,
      ...data,
      createdAt: toDateFirebase(doc, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(doc, data, 'updatedAt').toDate(),
      }),
    });
  });
  return aiAgents;
};

function* syncAiAgentsSaga() {
  const countryId = yield select((state) => state.Dashboard.countryId);

  const aiAgentsRef = query(
    collection(db, 'aiAgents'),
    where('countryId', '==', countryId),
    orderBy('createdAt', 'desc'),
  );

  const task = yield fork(rsf.firestore.syncCollection, aiAgentsRef, {
    successActionCreator: actions.syncAiAgentsSuccess,
    failureActionCreator: actions.syncAiAgentsFailure,
    transform: (payload) => aiAgentTransformer(payload),
  });

  yield take(types.RESET_AI_AGENT_STATE);
  yield cancel(task);
}

export function* createAiAgentSaga({ aiAgent }) {
  try {
    const countryId = yield select((state) => state.Dashboard.countryId);

    yield call(rsf.firestore.addDocument, collection(db, 'aiAgents'), {
      ...aiAgent,
      countryId,
      active: aiAgent.active ?? true,
      createdAt: serverTimestamp(),
    });

    yield put(actions.createAiAgentSuccess());
    toastr.success(i18n.t('AI Agent created!'), '');
  } catch (error) {
    yield put(actions.createAiAgentFailure(error));
    toastr.error(error.message, 'Error');
  }
}

export function* updateAiAgentSaga({ aiAgent }) {
  try {
    // channel leaves the payload: it is immutable after creation, and the
    // disabled select in the form is not enough to enforce it. Any other
    // caller, or a regression of the form, could change the channel of an
    // agent already in use — and with it costs, consent and transport of
    // every acquisition order linked to it.
    //
    // countryId leaves it for a harder reason: it is the tenant. Writing the
    // currently selected country back onto an existing agent would move that
    // agent into another tenant whenever the modal was opened before
    // switching country, because switching does not clear selectedAiAgent.
    // Ownership is decided once, on create.
    const { id, channel, countryId, ...aiAgentData } = aiAgent;
    if (!id) throw new Error('AI Agent not found');

    yield call(
      rsf.firestore.setDocument,
      doc(db, 'aiAgents', id),
      {
        ...aiAgentData,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    yield put(actions.updateAiAgentSuccess());
    toastr.success(i18n.t('AI Agent updated!'), '');
  } catch (error) {
    yield put(actions.updateAiAgentFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* aiAgentSaga() {
  yield all([
    takeLatest(types.SYNC_AI_AGENTS.REQUEST, syncAiAgentsSaga),
    takeLeading(types.CREATE_AI_AGENT.REQUEST, createAiAgentSaga),
    takeLeading(types.UPDATE_AI_AGENT.REQUEST, updateAiAgentSaga),
  ]);
}

export default aiAgentSaga;
